import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/StatCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { TransferCallLog, RefundDialog } from "@/components/TransferCallLog";
import { LiveTransferLeads } from "@/components/LiveTransferLeads";
import { LiveTransferZapTest } from "@/components/LiveTransferZapTest";
import { Wallet, PhoneForwarded, Loader2 } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const AddMoney = () => {
  const [amt, setAmt] = useState(100);
  const [busy, setBusy] = useState(false);
  const pay = async () => {
    setBusy(true);
    try { const { data } = await api.post("/payments/wallet-checkout", { amount: Number(amt), origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (e) { toast.error(apiError(e)); setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-end gap-2" data-testid="wallet-add-money">
      {[100, 250, 500].map((v) => <Button key={v} variant="outline" size="sm" className={Number(amt) === v ? "border-slate-900 bg-slate-900 text-white hover:bg-slate-800 hover:text-white" : ""} onClick={() => setAmt(v)} data-testid={`wallet-preset-${v}`}>${v}</Button>)}
      <div><Label className="text-xs">Amount</Label><Input type="number" min="25" value={amt} onChange={(e) => setAmt(e.target.value)} className="w-28" data-testid="wallet-amount-input" /></div>
      <Button onClick={pay} disabled={busy} data-testid="wallet-add-btn">{busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Wallet className="mr-1.5 h-4 w-4" />}Add lead money</Button>
    </div>
  );
};

const TransferSettings = ({ w, reload }) => {
  const [bid, setBid] = useState(w.bid);
  const [on, setOn] = useState(w.enabled);
  const save = async () => {
    try { await api.put("/wallet/settings", { enabled: on, ...(w.bidding ? { bid: Number(bid) } : {}) }); toast.success("Live transfer settings saved"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const line = w.eligible
    ? (w.ahead === 0 ? "You're next in line for a live transfer." : `${w.ahead} MLO${w.ahead === 1 ? " is" : "s are"} ahead of you for the next live transfer.`)
    : `You're being skipped right now: ${w.skip_reason}.`;
  return (
    <Card className="mb-5 p-6" data-testid="live-transfer-card">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900"><PhoneForwarded className="h-5 w-5" />Live transfers</h3>
        <Badge className={`border-0 ${w.eligible ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}`} data-testid="live-transfer-status">{w.eligible ? "Eligible - receiving transfers" : "Not receiving transfers"}</Badge>
      </div>
      <p className="mb-4 text-sm text-slate-600">Calls are forwarded straight to your phone with no screening. If you don't answer, the next MLO in line is rung, and if nobody answers Ava books an appointment. You're charged for every transfer you're sent, answered or not.</p>
      {w.free && <Badge className="mb-3 border-0 bg-indigo-100 text-indigo-800" data-testid="live-transfer-free-badge">Free live transfers - you're not charged</Badge>}
      {!w.has_phone && <p className="mb-3 text-sm font-semibold text-red-600" data-testid="live-transfer-no-phone">Add your phone number in My Profile to receive live transfers.</p>}
      <div className="mb-4 grid gap-3 sm:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3" data-testid="live-transfer-position">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Your place in line</div>
          <div className="font-display text-2xl font-extrabold text-slate-900" data-testid="live-transfer-position-value">{w.eligible ? (w.ahead === 0 ? "Next up" : `#${w.ahead + 1}`) : "Skipped"}</div>
          <div className="text-xs text-slate-600" data-testid="live-transfer-position-note">{line}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3" data-testid="live-transfer-price">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{w.bidding ? "Current highest bid" : "Your price per transfer"}</div>
          <div className="font-display text-2xl font-extrabold text-slate-900" data-testid="live-transfer-price-value">{w.bidding ? (w.top_bid ? money(w.top_bid) : "No active bids yet") : w.free ? "Free" : money(w.price)}</div>
          {!w.bidding && <div className="text-xs text-slate-600">Set by your administrator</div>}
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-4">
        {w.bidding && <div><Label className="text-xs">Your bid per transfer (min ${w.min_bid})</Label><Input type="number" min={w.min_bid} value={bid} onChange={(e) => setBid(e.target.value)} className="w-36" data-testid="live-transfer-bid" /></div>}
        <label className="flex items-center gap-2 pb-2 text-sm"><Switch checked={on} onCheckedChange={setOn} data-testid="live-transfer-toggle" />Receive live transfers</label>
        <Button onClick={save} data-testid="live-transfer-save">Save</Button>
      </div>
    </Card>
  );
};

const History = ({ tx, reload }) => {
  const [req, setReq] = useState(null);
  return (
  <Card className="p-6" data-testid="wallet-history">
    <h3 className="mb-1 font-display font-bold text-slate-900">Activity</h3>
    <p className="mb-3 text-xs text-slate-500">Charged for a live transfer that has no recording? Request a refund on the charge here.</p>
    {!tx.length && <p className="text-sm text-slate-500">No activity yet.</p>}
    {tx.map((t, i) => (
      <div key={t.id || i} className="flex items-center justify-between gap-3 border-b border-slate-100 py-2 text-sm last:border-0" data-testid={`wallet-tx-${i}`}>
        <span className="text-slate-700">{t.note}<span className="block text-xs text-slate-400">{new Date(t.created_at).toLocaleString()}</span></span>
        <span className="flex items-center gap-2">
          {t.refund_status && <Badge className="border-0 bg-slate-100 capitalize text-slate-700" data-testid={`wallet-tx-refund-status-${i}`}>Refund {t.refund_status}</Badge>}
          {t.can_request && <Button size="sm" variant="outline" onClick={() => setReq(t)} data-testid={`wallet-tx-refund-${i}`}>{t.refund_status === "denied" ? "Request again" : "Request refund"}</Button>}
          <span className={`font-semibold ${t.amount < 0 ? "text-slate-900" : "text-emerald-700"}`}>{t.amount < 0 ? "-" : "+"}{money(Math.abs(t.amount))}</span>
        </span>
      </div>
    ))}
    {req && <RefundDialog call={{ call_id: req.id, charge: -req.amount }} url={`/live-transfers/charges/${req.id}/refund-request`} onClose={() => setReq(null)} onDone={() => { setReq(null); reload(); }} />}
  </Card>
  );
};

export default function LeadWallet() {
  const [w, setW] = useState(null);
  const [ltKey, setLtKey] = useState(0);
  const load = () => api.get("/wallet").then((r) => setW(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);
  if (!w) return null;
  return (
    <div data-testid="lead-wallet-page">
      <PageHeader title="Live Transfers" subtitle="Load lead money to receive live transfers." />
      <Card className="mb-5 p-6">
        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Balance</div>
        <div className="mb-4 font-display text-4xl font-extrabold text-slate-900" data-testid="wallet-balance">{money(w.balance)}</div>
        <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="wallet-eligibility-note">
          Once you have at least a <b>$100 balance</b> in your account, you're eligible for <b>live transfers</b>. {w.bidding ? <>Live transfers go to the <b>highest bidder</b>.</> : <>Live transfers go to MLOs <b>in the order your administrator sets</b>; after you receive one you move to the back of the line.</>}
        </div>
        <AddMoney />
      </Card>
      <TransferSettings w={w} reload={load} />
      <LiveTransferZapTest onArrived={() => setLtKey((k) => k + 1)} />
      <LiveTransferLeads key={ltKey} />
      <TransferCallLog />
      <History tx={w.transactions} reload={load} />
    </div>
  );
}
